import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Download, Save } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money, http, apiError } from "@/lib/api";
import { exportCsv } from "@/lib/exporters";
import { PatientPhotos } from "@/components/VisitImages";
import Stat from "@/components/Stat";
import { toast } from "sonner";

export default function PatientProfile() {
  const { id } = useParams();
  const { data, showMoney, isOwner, save, remove, clinic } = useApp();
  const patient = data.patients.find((p) => p.id === id);
  const [edit, setEdit] = useState(null);
  const [appt, setAppt] = useState((patient?.next_appointment || "").slice(0, 10));
  const [apptTime, setApptTime] = useState(patient?.next_appointment_time || "10:00");
  const [allVisits, setAllVisits] = useState(false);
  const VISITS_SHOWN = 8;

  const visits = useMemo(
    () => data.visits.filter((v) => v.patient_id === id).sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [data.visits, id]
  );

  if (!patient) return <p className="dm-empty" data-testid="patient-missing">No patients found</p>;

  const consultantOf = (cid) => data.consultants.find((c) => c.id === cid)?.name || "—";
  const totals = visits.reduce((a, v) => ({
    clinic: a.clinic + Number(v.clinic_share || 0),
    fees: a.fees + Number(v.consultant_fee || 0),
    lab: a.lab + Number(v.lab_cost || 0),
  }), { clinic: 0, fees: 0, lab: 0 });

  const saveAppt = async () => {
    await save("patients", { ...patient, next_appointment: appt || null });
    toast.success("Next appointment updated");
  };

  const sendReminder = null;

  const saveEdit = async (e) => {
    e.preventDefault();
    await save("visits", {
      ...edit,
      clinic_share: Number(edit.clinic_share || 0),
      consultant_fee: Number(edit.consultant_fee || 0),
      lab_cost: Number(edit.lab_cost || 0),
      total_charged: Number(edit.total_charged || 0),
    });
    toast.success("Visit saved");
    setEdit(null);
  };

  const deleteVisit = async () => {
    await remove("visits", edit.id);
    toast.success("Visit deleted");
    setEdit(null);
  };

  return (
    <div className="space-y-5" data-testid="patient-profile">
      <Link to="/app" data-testid="profile-back" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-slate-800">
        <ArrowLeft size={15} /> Back
      </Link>

      <div className="dm-panel flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between md:p-5">
        <div className="flex items-center gap-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--dm-accent-soft)] font-head text-lg font-bold dm-accent-text">
            {(patient.name || "?").slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h1 className="truncate dm-page-title">{patient.name}</h1>
            <p className="mt-1 text-sm text-slate-600">
              ID <span className="font-mono">{patient.code ?? "—"}</span>
              {patient.age ? ` · ${patient.age}` : ""}{patient.gender ? ` · ${patient.gender}` : ""} · {patient.phone || "No phone"} · {visits.length} visits
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/app/log-visit?patient=${patient.id}`} data-testid="profile-add-visit" className="dm-btn-primary"><Plus size={15} /> Add visit</Link>
        </div>
      </div>

      <div className="dm-panel flex flex-col gap-3 p-4 sm:flex-row sm:items-end">
        <div className="flex-1">
          <p className="label-xs mb-1.5">Next appointment</p>
          <input type="date" data-testid="profile-appointment-input" className="dm-input" value={appt || ""} onChange={(e) => setAppt(e.target.value)} />
        </div>
        <div className="sm:w-40 hidden" />
        <button data-testid="profile-appointment-save" onClick={saveAppt} className="dm-btn-ghost"><Save size={15} /> Save</button>
      </div>

      {showMoney && (
        <div className="grid grid-cols-3 gap-3" data-testid="profile-financials">
          <Stat label="Clinic Share" value={money(totals.clinic)} />
          <Stat label="Consultant Fee" value={money(totals.fees)} />
          <Stat label="Lab Cost" value={money(totals.lab)} />
        </div>
      )}

      <div className="dm-panel overflow-hidden">
        <div className="dm-panel-head">
          <h2 className="font-head text-lg font-semibold text-slate-800">Visit History</h2>
          {isOwner && (
          <button data-testid="profile-export-btn" className="dm-btn-quiet !py-1.5 !text-xs"
            onClick={() => exportCsv(visits.map((v) => ({ Date: v.date, Treatment: v.treatment, Diagnosis: v.diagnosis,
              Consultant: consultantOf(v.consultant_id),
              ...(showMoney ? { "Clinic Share": v.clinic_share, "Consultant Fee": v.consultant_fee, "Lab Cost": v.lab_cost } : {}),
              Remarks: v.remarks })), `visits-${patient.name}`)}>
            <Download size={13} /> CSV
          </button>
          )}
        </div>
        {visits.length === 0 ? (
          <div className="dm-empty m-4" data-testid="profile-empty">No visits</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="dm-table [&_td]:whitespace-nowrap" data-testid="visit-history">
              <thead>
                <tr>
                  <th>Date</th><th>Treatment</th><th>Diagnosis</th><th>Consultant</th>
                  <th className="!text-right">Clinic Share</th><th className="!text-right">Consultant Fee</th><th className="!text-right">Lab Cost</th><th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {(allVisits ? visits : visits.slice(0, VISITS_SHOWN)).map((v) => (
                  <tr key={v.id} data-testid={`visit-row-${v.id}`} onClick={() => isOwner && setEdit({ ...v })}
                    className={isOwner ? "cursor-pointer" : ""}>
                    <td className="whitespace-nowrap font-mono text-slate-600">{v.date}</td>
                    <td className="text-slate-800">{v.treatment || "—"}</td>
                    <td>{v.diagnosis || "—"}</td>
                    <td>{consultantOf(v.consultant_id)}</td>
                    <td className="num text-slate-800">{showMoney ? money(v.clinic_share) : "•••"}</td>
                    <td className="num">{showMoney ? money(v.consultant_fee) : "•••"}</td>
                    <td className="num">{showMoney ? money(v.lab_cost) : "•••"}</td>
                    <td className="max-w-[200px] truncate text-slate-600">{v.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
              {showMoney && (
                <tfoot>
                  <tr>
                    <td colSpan={4}>Total</td>
                    <td className="num text-slate-800">{money(totals.clinic)}</td>
                    <td className="num">{money(totals.fees)}</td>
                    <td className="num">{money(totals.lab)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {visits.length > VISITS_SHOWN && (
        <div className="-mt-3 text-center">
          <button onClick={() => setAllVisits(!allVisits)} data-testid="profile-visits-toggle" className="dm-btn-quiet !py-1.5 !text-xs">
            {allVisits ? "Show latest visits only" : `Show all ${visits.length} visits`}
          </button>
        </div>
      )}

      <PatientPhotos patientId={id} visits={visits} />

      {edit && (
        <div className="fixed inset-0 z-40 grid place-items-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm">
          <form onSubmit={saveEdit} data-testid="edit-modal" className="dm-panel w-full max-w-2xl bg-white p-6" style={{ boxShadow: "0 8px 32px rgba(0,0,0,.55)" }}>
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-head text-xl font-bold text-slate-900">Edit Visit</h3>
              <button type="button" onClick={() => setEdit(null)} data-testid="edit-close" className="dm-btn-quiet !p-2">×</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="label-xs mb-1.5">Date</p>
                <input type="date" data-testid="edit-date" className="dm-input" value={edit.date || ""} onChange={(e) => setEdit({ ...edit, date: e.target.value })} />
              </div>
              <div>
                <p className="label-xs mb-1.5">Treatment</p>
                <select data-testid="edit-treatment" className="dm-input" value={edit.treatment || ""} onChange={(e) => setEdit({ ...edit, treatment: e.target.value })}>
                  <option value="">— Select treatment —</option>
                  {(clinic?.treatments || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <p className="label-xs mb-1.5">Diagnosis</p>
                <input data-testid="edit-diagnosis" className="dm-input" value={edit.diagnosis || ""} onChange={(e) => setEdit({ ...edit, diagnosis: e.target.value })} />
              </div>
              <div>
                <p className="label-xs mb-1.5">Consultant</p>
                <select data-testid="edit-consultant" className="dm-input" value={edit.consultant_id || ""} onChange={(e) => setEdit({ ...edit, consultant_id: e.target.value || null })}>
                  <option value="">— Select —</option>
                  {data.consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <p className="label-xs mb-1.5">Clinic Share (₹)</p>
                <input type="number" data-testid="edit-clinic-share" className="dm-input font-mono" value={edit.clinic_share ?? ""} onChange={(e) => setEdit({ ...edit, clinic_share: e.target.value })} />
              </div>
              <div>
                <p className="label-xs mb-1.5">Consultant Fee (₹)</p>
                <input type="number" data-testid="edit-cons-fee" className="dm-input font-mono" value={edit.consultant_fee ?? ""} onChange={(e) => setEdit({ ...edit, consultant_fee: e.target.value })} />
              </div>
              <div>
                <p className="label-xs mb-1.5">Lab Cost (₹)</p>
                <input type="number" data-testid="edit-lab-cost" className="dm-input font-mono" value={edit.lab_cost ?? ""} onChange={(e) => setEdit({ ...edit, lab_cost: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <p className="label-xs mb-1.5">Remarks</p>
                <textarea rows={2} data-testid="edit-remarks" className="dm-input" value={edit.remarks || ""} onChange={(e) => setEdit({ ...edit, remarks: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between gap-2">
              <button type="button" data-testid="edit-delete" onClick={deleteVisit} className="dm-btn-danger"><Trash2 size={15} /> Delete Visit</button>
              <button type="submit" data-testid="edit-save" className="dm-btn-primary">✓ Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
